import {
  useNavigate,
  useParams,
  useLocation,
  generatePath,
} from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import { SecurityLockForm } from '@ui/src';
import { clearLockPanelState } from '../store/slices/security';
import {
  useLazyGetCustomerLockItemQuery,
  useUpdateCustomerLockMutation,
  useCreateCustomerLockMutation,
  useLazyGetCustomerLocksQuery,
  useLazyGetCustomerLockItemAuditLogsQuery,
  useLazyGetCustomerActiveLocksQuery,
} from '../store/services/individual';
import {
  schema,
  columns,
  columnsLabels,
  filtersSchema,
} from './variables/lock-item-logs';

import ROUTES from '../../../routes';

const IndividualCustomerSecurityLockForm = ({
  type,
}: {
  type: 'locks' | 'logs' | 'devices';
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { customerId } = useParams();
  const { search } = useLocation();

  const [getLockItem, { isLoading, isFetching }] =
    useLazyGetCustomerLockItemQuery();
  const [getLocks] = useLazyGetCustomerLocksQuery();
  const [create, { isLoading: requestInProgress, isSuccess }] =
    useCreateCustomerLockMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateIsSuccess }] =
    useUpdateCustomerLockMutation();
  const [
    getList,
    { isFetching: lockItemLogsIsFetching, isLoading: lockItemLogsIsLoading },
  ] = useLazyGetCustomerLockItemAuditLogsQuery();
  const [getCustomerActiveLock] = useLazyGetCustomerActiveLocksQuery();

  const {
    individualCustomers: {
      info: {
        general: { baseInformation },
        security: {
          lockItem,
          locksTakeCount,
          lockItemAuditLogs,
          lockItemAuditLogsPagination,
        },
      },
    },
    root: { activePermissions },
  } = useAppSelector((state) => state);

  const getCustomerActiveLockAction = useCallback(() => {
    getCustomerActiveLock(baseInformation?.userId as string);
  }, []);

  useEffect(() => {
    if (isSuccess || updateIsSuccess) {
      getCustomerActiveLockAction();
    }
  }, [isSuccess, updateIsSuccess]);

  return (
    <SecurityLockForm
      getLockItem={getLockItem}
      getLocks={getLocks}
      create={create}
      update={update}
      lockItem={lockItem}
      takeCount={locksTakeCount}
      activePermissions={activePermissions}
      clearLockPanelState={() => dispatch(clearLockPanelState())}
      onClose={() => {
        let link = '';

        if (type === 'locks') {
          link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS, {
            customerId: customerId as string,
          });
        } else if (type === 'logs') {
          link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS, {
            customerId: customerId as string,
          });
        } else if (type === 'devices') {
          link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES, {
            customerId: customerId as string,
          });
        }

        navigate(`${link}${search || ''}`);
      }}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isFetching={isFetching}
      requestInProgress={requestInProgress}
      updateInProgress={updateInProgress}
      userId={baseInformation?.userId}
      lockItemAuditLogsPagination={lockItemAuditLogsPagination}
      lockItemAuditLogs={lockItemAuditLogs}
      getLockItemLogsList={getList}
      lockItemLogsSchema={schema}
      lockItemLogsFiltersSchema={filtersSchema}
      lockItemLogsColumns={columns}
      lockItemLogsColumnsLabels={columnsLabels}
      lockItemLogsIsLoading={lockItemLogsIsLoading}
      lockItemLogsIsFetching={lockItemLogsIsFetching}
      withoutUserSelector
    />
  );
};

export default IndividualCustomerSecurityLockForm;
